import { Fragment, useState } from 'react';

import { Combobox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

import { REGEX } from '@lessonPlanner/regex';

import { formClasses } from './utils/FormStyles';

interface ComboboxOption {
  id: string | number;
  name: string;
  [key: string]: unknown;
}

interface ComboboxFieldProps {
  value: ComboboxOption;
  onChange: (value: ComboboxOption) => void;
  options: ComboboxOption[];
  label?: React.ReactNode;
  errorMessage?: string;
  id?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  link?: string;
  tooltip?: string;
  highlight?: boolean;
}

export function ComboboxField({
  value,
  onChange,
  options,
  label,
  errorMessage,
  id,
  placeholder = 'Select an option',
  className,
  disabled = false,
  tooltip = '',
  highlight = false,
}: ComboboxFieldProps) {
  const [query, setQuery] = useState('');
  const [showTooltip, setShowTooltip] = useState(false);

  const filteredOptions =
    query === ''
      ? options
      : options.filter((option) =>
          option.name
            .toLowerCase()
            .replace(REGEX.REMOVE_WHITESPACE, '')
            .includes(query.toLowerCase().replace(REGEX.REMOVE_WHITESPACE, '')),
        );

  const inputClassName = clsx(
    formClasses,
    'pr-10 h-[38px]',
    { 'cursor-not-allowed': disabled },
    { 'border-red-500 focus:border-red-700': errorMessage },
  );

  return (
    <div className={`${className} flex h-full flex-row items-center gap-2`}>
      {label && <label htmlFor={id}>{label}:</label>}
      <Combobox disabled={disabled} value={value} onChange={onChange}>
        <div
          className="relative w-full"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <div className="relative">
            <Combobox.Input
              placeholder={placeholder}
              className={clsx(
                'h-[38px] w-full bg-white p-0',
                highlight &&
                  'animate-ring-pulse ring-2 ring-blue-500 ring-offset-2',
                className,
                inputClassName,
              )}
              displayValue={(option: ComboboxOption | null) => {
                return option?.name || '';
              }}
              onChange={(event) => setQuery(event.target.value)}
            />
            <Combobox.Button
              className="absolute inset-y-0 right-0 flex items-center pr-2"
              onClick={() => setShowTooltip(false)}
            >
              <ChevronUpDownIcon
                aria-hidden="true"
                className="h-5 w-5 text-gray-400"
              />
            </Combobox.Button>
          </div>
          {tooltip && showTooltip && (
            <div className="absolute left-0 z-50 mt-2 rounded-md border border-gray-200 bg-white p-2 text-sm text-gray-600 shadow-md">
              {tooltip}
            </div>
          )}
          <Transition
            afterLeave={() => setQuery('')}
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Combobox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
              {filteredOptions.length === 0 && query !== '' ? (
                <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                  Nothing found.
                </div>
              ) : (
                filteredOptions.map((option) => (
                  <Combobox.Option
                    key={option.id}
                    value={option}
                    className={({ active }) =>
                      clsx(
                        'relative cursor-default select-none py-2 pl-10 pr-4',
                        {
                          'bg-blue-600 text-white': active,
                          'text-gray-900': !active,
                        },
                      )
                    }
                    onClick={() => setShowTooltip(false)}
                  >
                    {({ selected, active }) => (
                      <>
                        <span
                          className={clsx('block truncate', {
                            'font-medium': selected,
                            'font-normal': !selected,
                          })}
                          style={{
                            fontFamily: option.fontFamily
                              ? String(option.fontFamily)
                              : 'inherit',
                          }}
                        >
                          {option.name}
                        </span>
                        {selected ? (
                          <span
                            className={clsx(
                              'absolute inset-y-0 left-0 flex items-center pl-3',
                              {
                                'text-white': active,
                                'text-blue-600': !active,
                              },
                            )}
                          >
                            <CheckIcon aria-hidden="true" className="h-5 w-5" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>
      {errorMessage && <p className="text-red-500">{errorMessage}</p>}
    </div>
  );
}
